/**
 * Document file-type visuals. `docTypeSpec` maps a MIME type to an icon + tone
 * so PDFs, images, sheets, and text read at a glance. `DocAvatar` is the small
 * tile used in list rows; the Dropbox-style grid tile uses the bare icon.
 */
import { FileText, Image as ImageIcon, Sheet, FileType2 } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@flint/ui";

export interface DocTypeSpec {
  icon: LucideIcon;
  /** Tinted tile background + foreground (for the avatar). */
  tile: string;
  /** Foreground colour only (for a bare icon on a neutral preview). */
  fg: string;
}

export function docTypeSpec(contentType: string): DocTypeSpec {
  if (contentType === "application/pdf")
    return { icon: FileText, tile: "bg-bad-50 text-bad", fg: "text-bad" };
  if (contentType.startsWith("image/"))
    return { icon: ImageIcon, tile: "bg-sky-50 text-sky-700", fg: "text-sky-700" };
  if (contentType.includes("spreadsheet") || contentType.includes("excel") || contentType === "text/csv")
    return { icon: Sheet, tile: "bg-ok-50 text-ok", fg: "text-ok" };
  if (contentType.includes("word") || contentType.includes("msword"))
    return { icon: FileType2, tile: "bg-sky-50 text-sky-700", fg: "text-sky-700" };
  return { icon: FileText, tile: "bg-raised text-muted", fg: "text-muted" };
}

const SIZE = { sm: "h-8 w-8", md: "h-11 w-11", lg: "h-14 w-14" } as const;
const ICON_SIZE = { sm: 15, md: 19, lg: 24 } as const;

export function DocAvatar({ contentType, size = "md" }: { contentType: string; size?: "sm" | "md" | "lg" }) {
  const spec = docTypeSpec(contentType);
  const Icon = spec.icon;
  return (
    <span
      aria-hidden
      className={cn("inline-flex shrink-0 items-center justify-center rounded-md shadow-sm", SIZE[size], spec.tile)}
    >
      <Icon size={ICON_SIZE[size]} strokeWidth={2} />
    </span>
  );
}
